// frontend/src/components/Navbar.js
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  AdjustmentsIcon,
  ChevronDownIcon,
  MenuIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  XIcon,
} from "@heroicons/react/solid";
import { useAuth } from "../hooks/useAuth";

const publicLinks = [
  { to: "/", label: "Home", mobileOnly: true },
  { to: "/about", label: "About" },
  { to: "/consultancies", label: "Consultancies", hideForOwner: true },
  { to: "/blog/comparing-and-trust-abroadjacks", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const studentLinks = [
  { to: "/student/dashboard", label: "My Study Abroad", shortLabel: "Home" },
  { to: "/consultancies", label: "Explore Consultancies", shortLabel: "Explore" },
  { to: "/offers", label: "Offers" },
  { to: "/wishlist", label: "Wishlist" },
  { to: "/student/activity", label: "My Activity", shortLabel: "Activity" },
];

const accountLinks = [
  { to: "/student/profile", label: "Profile", icon: UserCircleIcon },
  { to: "/student/preferences", label: "Study Preferences", icon: AdjustmentsIcon },
  { to: "/contact", label: "Help & Support", icon: QuestionMarkCircleIcon },
];

export default function Navbar() {
  const [logoFailed, setLogoFailed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudent = user?.role === "student";

  const isPathActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navLinks = isStudent
    ? studentLinks
    : publicLinks.filter((link) => !(link.hideForOwner && user?.role === "owner"));

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    setAccountOpen(false);
    navigate("/", { replace: true });
  };

  const handleBrandClick = () => {
    const destination = isStudent ? "/student/dashboard" : "/";
    if (location.pathname === destination) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(destination);
    }
  };

  const accountLabel = (user?.name || user?.email || "").trim().split(/\s+/)[0] || "My Account";
  const initials =
    (user?.name || user?.email || "AJ")
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "AJ";

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setAccountOpen(false);
    };
    const onPointerDown = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, []);

  const renderUserActions = () => {
    if (!user) {
      return (
        <>
          <Link
            to="/register"
            className={`inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition ${
              isPathActive("/register")
                ? "bg-white/10 text-white"
                : "text-white/80 hover:bg-white/[0.08] hover:text-white"
            }`}
          >
            Signup
          </Link>
          <Link
            to="/login"
            className={`inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ${
              isPathActive("/login")
                ? "bg-white/10 text-white"
                : "text-white/80 hover:bg-white/[0.08] hover:text-white"
            } transition`}
          >
            Login
          </Link>
        </>
      );
    }

    if (isStudent) {
      return (
        <div className="relative" ref={accountRef}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            onClick={() => setAccountOpen((open) => !open)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-white/15 bg-white/[0.06] px-2.5 py-2 text-sm font-semibold text-white transition hover:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-300/15 text-xs font-bold text-cyan-100 ring-1 ring-cyan-200/25">
              {initials}
            </span>
            <span className="hidden max-w-[130px] truncate xl:inline">{accountLabel}</span>
            <ChevronDownIcon className={`h-4 w-4 transition ${accountOpen ? "rotate-180" : ""}`} />
          </button>

          {accountOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-3 w-64 overflow-hidden rounded-xl border border-white/12 bg-slate-950/98 p-2 shadow-2xl shadow-slate-950/40"
            >
              <div className="px-3 py-2">
                <p className="truncate text-sm font-semibold text-white">{user.name || "Student account"}</p>
                <p className="truncate text-xs text-slate-400">{user.email || "Manage your AbroadJacks account"}</p>
              </div>
              <div className="my-1 border-t border-white/10" />
              {accountLinks.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  role="menuitem"
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 transition hover:bg-white/[0.08] hover:text-white"
                >
                  <Icon className="h-4 w-4 text-cyan-200" />
                  {label}
                </Link>
              ))}
              <div className="my-1 border-t border-white/10" />
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-rose-100 transition hover:bg-rose-400/10"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      );
    }

    const profileLink =
      user.role === "admin"
        ? "/admin/consultancies"
        : user.role === "owner"
        ? "/owner/dashboard"
        : null;

    return (
      <>
        {profileLink && (
          <Link to={profileLink} className="inline-flex min-h-[44px] items-center rounded-lg px-3 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/[0.08] hover:text-white">
            {user.role === "admin"
              ? "Admin Panel"
              : user.role === "owner"
              ? "Owner Panel"
              : "Dashboard"}
          </Link>
        )}
        <span className="hidden max-w-[170px] truncate rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm text-white md:inline">
          {user.name || user.email}
        </span>
        <button
          onClick={handleLogout}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-rose-300/30 px-4 py-2 text-sm font-semibold text-rose-100 transition hover:bg-rose-400/10"
        >
          Logout
        </button>
      </>
    );
  };

  return (
    <nav
      className={`w-full sticky top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? "bg-slate-950/78 backdrop-blur-xl border-b border-white/15 shadow-[0_10px_28px_rgba(2,6,23,0.42)]"
          : "bg-slate-950/70 backdrop-blur border-b border-white/10"
      }`}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between gap-5 px-4 transition-all duration-300 sm:px-6 ${
          isScrolled ? "py-2" : "py-3"
        }`}
      >
        <button
          type="button"
          aria-label="Go to AbroadJacks home"
          onClick={handleBrandClick}
          className="group flex max-w-[calc(100%-3.5rem)] min-w-0 items-center gap-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
        >
          {!logoFailed && (
            <img
              src="/broadjacks-logo.png"
              alt="AbroadJacks study abroad services logo"
              className="h-8 w-8 flex-none rounded-md object-contain sm:h-10 sm:w-10 lg:h-11 lg:w-11"
              onError={() => setLogoFailed(true)}
            />
          )}
          <div className="min-w-0 flex-1 text-left">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="truncate text-base font-bold leading-none tracking-tight text-white min-[360px]:text-lg sm:text-2xl">
                AbroadJacks
              </p>
            </div>
            <p className="mt-1 hidden truncate text-[9px] uppercase tracking-[3px] text-white/60 min-[380px]:block sm:text-[10px] sm:tracking-[6px]">
              Study Abroad Services
            </p>
          </div>
        </button>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.filter((link) => !link.mobileOnly).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isPathActive(link.to)
                  ? "bg-white/10 text-white"
                  : "text-white/75 hover:bg-white/[0.08] hover:text-white"
              }`}
            >
              {isStudent ? link.shortLabel || link.label : link.label}
            </Link>
          ))}
          {renderUserActions()}
        </div>

        <button
          type="button"
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileOpen}
          aria-controls="abroadjacks-mobile-menu"
          onClick={() => setMobileOpen((open) => !open)}
          className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-white/15 text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-300/50 lg:hidden"
        >
          {mobileOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 top-[64px] z-50 bg-slate-950/70 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)}>
          <div
            id="abroadjacks-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            className="mx-3 mt-3 max-h-[calc(100svh-88px)] overflow-y-auto rounded-2xl border border-white/10 bg-slate-950 p-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grid gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`min-h-[46px] rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    isPathActive(link.to)
                      ? "bg-white/10 text-white"
                      : "text-white/80 hover:bg-white/[0.08] hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-3 grid gap-2 border-t border-white/10 pt-3">
              {isStudent ? (
                <>
                  {accountLinks.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      className={`flex min-h-[46px] items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition ${
                        isPathActive(to)
                          ? "bg-white/10 text-white"
                          : "text-white/80 hover:bg-white/[0.08] hover:text-white"
                      }`}
                    >
                      <Icon className="h-4 w-4 text-cyan-200" />
                      {label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="min-h-[46px] rounded-xl border border-rose-300/30 px-4 py-3 text-left text-sm font-semibold text-rose-100 transition hover:bg-rose-400/10"
                  >
                    Logout
                  </button>
                </>
              ) : (
                renderUserActions()
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
